/**
 * Settings (Beta-1 plan P4-01 / P4-02, Tim: gear → one Settings page) — tabs:
 *   Your Settings   profile, notification preferences, appearance        (/settings)
 *   Organization    structure, members & roles, invites, library, import (/settings/organization)
 *   <IA>            one tab per active Intelligent Assistant — DREW: OE thresholds (/settings/drew)
 * Tabs are routes so tours and deep links land on the right one by pathname.
 * The Organization and IA tabs follow the same access gate as the old Org Settings page.
 */
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import Settings from './Settings';
import OrgSettings from '../assistants/drew/pages/OrgSettings';
import { useDemoRole } from '../contexts/DemoRoleContext';
import { canAccessToolPath } from '../assistants/drew/utils/accessControl';
import { assistantSettingsTabs } from '../platform/settings/assistantSettingsTabs.jsx';

export const SETTINGS_PATHS = Object.freeze({ you: '/settings', organization: '/settings/organization', drew: '/settings/drew' });

function SettingsPage() {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const { persona } = useDemoRole();
  const orgAllowed = canAccessToolPath(persona?.id, '/drew/settings');

  const tabs = useMemo(() => {
    const list = [{ key: 'you', label: 'Your Settings', icon: <PersonIcon fontSize="small" />, Component: () => <Settings embedded /> }];
    if (orgAllowed) {
      list.push({ key: 'organization', label: 'Organization', icon: <BusinessIcon fontSize="small" />, Component: () => <OrgSettings section="organization" embedded /> });
      for (const t of assistantSettingsTabs()) {
        list.push({
          key: t.key,
          label: t.label,
          icon: <Box component="img" src={t.avatar} alt="" sx={{ width: 22, height: 22, borderRadius: '50%' }} />,
          Component: t.Component,
        });
      }
    }
    return list;
  }, [orgAllowed]);

  const current = tabs.find((t) => t.key === (tabParam || 'you')) || tabs[0];
  const Active = current.Component;

  // A tab this viewer cannot see (or an unknown one) falls back to Your Settings — and the URL follows.
  useEffect(() => {
    if (tabParam && current.key !== tabParam) navigate(SETTINGS_PATHS.you, { replace: true });
  }, [tabParam, current.key, navigate]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Tabs
        value={current.key}
        onChange={(_, key) => navigate(key === 'you' ? SETTINGS_PATHS.you : `/settings/${key}`)}
        aria-label="Settings sections"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} icon={t.icon} iconPosition="start" data-tour={`settings-tab-${t.key}`} sx={{ minHeight: 48, textTransform: 'none', fontWeight: 600 }} />
        ))}
      </Tabs>
      <Box role="tabpanel" aria-label={current.label}>
        <Active />
      </Box>
    </Box>
  );
}

export default SettingsPage;
